// ----------------------------------------------------------------------------
// Section: Site Header -- Mobile Search Toggle
// ----------------------------------------------------------------------------
//
// On mobile the Convermax search box collapses to a 44px magnifier next to
// the vehicle selector. Tapping it expands the inline search box over the
// "Select Your Vehicle" row (js--search-open) instead of letting Convermax
// open its fullscreen SearchBoxDialog, which it does whenever the inline
// input gains focus on a mobile user agent. Convermax's listeners live
// inside #cm-search-box, so stopping capture-phase focus events on the
// wrapper keeps the dialog closed while the inline input keeps working.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
var SEARCH_CONTROL_SELECTOR = '.header__search-control';
var SEARCH_INPUT_SELECTOR = '#cm-search-box input';

// Matches bp(laptop), where the search box is always expanded
var DESKTOP_MEDIA_QUERY = '(min-width: 1080px)';

// Classes
var SEARCH_OPEN_CLASS = 'js--search-open';
class iamHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.$searchControl = this.querySelector(SEARCH_CONTROL_SELECTOR);
    if (this.$searchControl === null) {
      return;
    }
    this.desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.onFocusCaptureRef = this.onFocusCapture.bind(this);
    this.onClickCaptureRef = this.onClickCapture.bind(this);
    this.onDocumentClickRef = this.onDocumentClick.bind(this);
    this.onDocumentFocusInRef = this.onDocumentFocusIn.bind(this);
    this.onKeydownRef = this.onKeydown.bind(this);
    this.closeRef = this.close.bind(this);
    this.$searchControl.addEventListener('focus', this.onFocusCaptureRef, true);
    this.$searchControl.addEventListener('focusin', this.onFocusCaptureRef, true);
    this.$searchControl.addEventListener('click', this.onClickCaptureRef, true);
    document.addEventListener('click', this.onDocumentClickRef);
    document.addEventListener('focusin', this.onDocumentFocusInRef);
    document.addEventListener('keydown', this.onKeydownRef);
    this.desktopQuery.addEventListener('change', this.closeRef);
  }
  get isOpen() {
    return this.classList.contains(SEARCH_OPEN_CLASS);
  }
  get overlay() {
    return this._overlay || (this._overlay = document.querySelector('#search-overlay'));
  }

  // syncResultsTop runs first so --header-search-results-top is always
  // fresh when Convermax reads it for the desktop dropdown top position.
  showOverlay() {
    this.syncResultsTop();
    if (this.overlay) this.overlay.style.display = 'block';
    // Keep the dropdown top anchored while the sticky header shifts
    // (announcement bar scrolling away changes getBoundingClientRect).
    this.syncResultsTopRef = this.syncResultsTopRef || this.syncResultsTop.bind(this);
    window.addEventListener('scroll', this.syncResultsTopRef, {
      passive: true
    });
  }
  hideOverlay() {
    if (this.overlay) this.overlay.style.display = 'none';
    if (this.syncResultsTopRef) window.removeEventListener('scroll', this.syncResultsTopRef);
  }
  open() {
    var _this$querySelector;
    this.classList.add(SEARCH_OPEN_CLASS);
    (_this$querySelector = this.querySelector(SEARCH_INPUT_SELECTOR)) === null || _this$querySelector === void 0 || _this$querySelector.focus();
    this.showOverlay();
  }
  close() {
    this.classList.remove(SEARCH_OPEN_CLASS);
    this.hideOverlay();
  }

  // Desktop: show overlay when the Convermax search input gains focus.
  // Fires on every focusin on the document; only acts when on desktop and
  // the focused element is inside #cm-search-box.
  onDocumentFocusIn(event) {
    var _document$querySelect;
    if (!this.desktopQuery.matches) return;
    if ((_document$querySelect = document.querySelector('#cm-search-box')) !== null && _document$querySelect !== void 0 && _document$querySelect.contains(event.target)) {
      this.showOverlay();
    }
  }

  // Anchor the fixed full-page results panel right under the search bar
  syncResultsTop() {
    this.style.setProperty('--header-search-results-top', "".concat(Math.round(this.getBoundingClientRect().bottom), "px"));
  }

  // Keep Convermax from seeing focus events on mobile so its fullscreen
  // search dialog never opens; the input itself still receives focus
  onFocusCapture(event) {
    if (!this.desktopQuery.matches) {
      event.stopPropagation();
    }
  }

  // The first tap on the collapsed magnifier expands the search box instead
  // of submitting the empty query
  onClickCapture(event) {
    if (this.desktopQuery.matches || this.isOpen) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.open();
  }
  onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }

    // Desktop: hide overlay when clicking outside the search box
    if (this.desktopQuery.matches) {
      var searchBox = document.querySelector('#cm-search-box');
      if (searchBox && !searchBox.contains(event.target)) {
        this.hideOverlay();
      }
    }
  }
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.hideOverlay();
    }
  }
  disconnectedCallback() {
    var _this$desktopQuery;
    if (this.$searchControl) {
      this.$searchControl.removeEventListener('focus', this.onFocusCaptureRef, true);
      this.$searchControl.removeEventListener('focusin', this.onFocusCaptureRef, true);
      this.$searchControl.removeEventListener('click', this.onClickCaptureRef, true);
    }
    document.removeEventListener('click', this.onDocumentClickRef);
    document.removeEventListener('focusin', this.onDocumentFocusInRef);
    document.removeEventListener('keydown', this.onKeydownRef);
    (_this$desktopQuery = this.desktopQuery) === null || _this$desktopQuery === void 0 || _this$desktopQuery.removeEventListener('change', this.closeRef);
  }
}
if (!customElements.get('iam-header-search')) {
  customElements.define('iam-header-search', iamHeaderSearch);
}